import { enableMapSet } from 'immer';
import { immer } from 'zustand/middleware/immer';
import { create } from 'zustand/react';
import { AgentDrawerModel, AgentDrawerSortModel } from '../types/types';
import { getAxios } from '../../../lib/axios';
import { AgentInboxEndpoints } from '../../../constants/endpoints/agentInboxEndpoints.ts';
import { sortActivitiesByAlertLevel } from '../utils/groupAndSortActivities';
import ActivityAlertLevel from '../../../enums/activities/ActivityAlertLevel';
import ActivityStatus from '../../../enums/activities/ActivityStatus';
import { useAgentDrawerHubStore } from '../hubs/AgentDrawerHub';

enableMapSet();

type AgentDrawerItem = {
  value: AgentDrawerModel;
  order?: number;
  changedValues?: Set<string>;
  selected?: boolean;
};

type AgentDrawerCounts = {
  Total: number;
  NewReply: number;
  Urgent: number;
  Today: number;
  Tomorrow: number;
  Upcoming: number;
  Waiting: number;
  Holding: number;
  WaitingForPayment: number;
};

type AgentDrawerStore = {
  activityMap: Map<number, AgentDrawerItem>;
  assigned: number[]; // ordered list for rendering
  counts: AgentDrawerCounts;
  newReplyActivityIds: Set<number>;

  filterModel: Partial<AgentDrawerSortModel>;
  userId?: string;
  expandAllOverride: boolean;
  openTabTarget: number | null;

  init: () => Promise<void>;
  handleUpdate: (item: AgentDrawerModel) => void;
  handleBulkUpdate: (items: AgentDrawerModel[]) => void;
  handleBulkRemove: (items: number[]) => void;
  handleRemove: (id: number) => void;

  updateCounts: () => void;
  addNewReply: (activityId: number) => void;
  removeNewReply: (activityId: number) => void;

  setFilterModel: (model: Partial<AgentDrawerSortModel>) => void;
  reset: () => void;
  setUserId: (id?: string) => void;
  setExpandAllOverride: (expand: boolean) => void;
  toggleOpenActivity: (id?: number) => void;
};

const calculateCounts = (
  activityMap: Map<number, AgentDrawerItem>,
  newReplyActivityIds: Set<number>
): AgentDrawerCounts => {
  const activities = Array.from(activityMap.values()).map((item) => item.value);

  return {
    Total: activities.length,
    NewReply: newReplyActivityIds.size,
    Urgent: activities.filter((a) => a.ActivityAlertLevel === ActivityAlertLevel.Urgent).length,
    Today: activities.filter((a) => a.ActivityAlertLevel === ActivityAlertLevel.Today).length,
    Tomorrow: activities.filter((a) => a.ActivityAlertLevel === ActivityAlertLevel.Tomorrow).length,
    Upcoming: activities.filter((a) => a.ActivityAlertLevel === ActivityAlertLevel.Upcoming).length,
    Waiting: activities.filter((a) => a.ActivityStatus === ActivityStatus.Waiting).length,
    Holding: activities.filter((a) => a.ActivityStatus === ActivityStatus.Holding).length,
    WaitingForPayment: activities.filter((a) => a.ActivityStatus === ActivityStatus.WaitingForPayment).length
  };
};

const applyOrder = (state: AgentDrawerStore) => {
  const baseArray = Array.from(state.activityMap.values()).flatMap((r) => r.value);
  const sorted = sortActivitiesByAlertLevel(baseArray, state.filterModel);
  sorted.forEach((item, idx) => {
    const exists = state.activityMap.get(item.Id);
    if (exists && exists?.order != idx) {
      state.activityMap.set(item.Id, {
        ...exists,
        order: idx
      });
    }
  });
};

export const useAgentDrawerStore = create<AgentDrawerStore>()(
  immer((set) => ({
    activityMap: new Map(),
    assigned: [],
    counts: {
      Total: 0,
      NewReply: 0,
      Urgent: 0,
      Today: 0,
      Tomorrow: 0,
      Upcoming: 0,
      Waiting: 0,
      Holding: 0,
      WaitingForPayment: 0
    },
    newReplyActivityIds: new Set(),

    filterModel: {},
    userId: undefined,
    expandAllOverride: false,
    openTabTarget: null,

    init: async () => {
      useAgentDrawerHubStore.getState().setIsUpdating(true);

      const { data: items } = await getAxios().get<AgentDrawerModel[]>(AgentInboxEndpoints.AgentDrawer.initial);

      set((state) => {
        state.activityMap = new Map();
        state.assigned = [];

        items.forEach((value) => {
          state.activityMap.set(value.Id, { value });
          state.assigned.push(value.Id);
        });

        applyOrder(state);
        state.counts = calculateCounts(state.activityMap, state.newReplyActivityIds);
      });

      useAgentDrawerHubStore.getState().setIsUpdating(false);
    },

    handleUpdate: (update) =>
      set((state) => {
        const oldItem = state.activityMap.get(update.Id);
        const changedValues = oldItem ? new Set(oldItem.changedValues) : new Set<string>();

        // Simple change detection - can be enhanced later
        if (oldItem && JSON.stringify(oldItem.value) !== JSON.stringify(update)) {
          changedValues.add('updated');
        }

        const newItem: AgentDrawerItem = {
          value: update,
          order: oldItem?.order,
          changedValues,
          selected: oldItem?.selected
        };

        state.activityMap.set(update.Id, newItem);
        applyOrder(state);
        state.counts = calculateCounts(state.activityMap, state.newReplyActivityIds);
      }),

    toggleOpenActivity: (id) =>
      set((state) => {
        state.openTabTarget = id ?? null;
      }),

    handleBulkUpdate: (items) => {
      set((state) => {
        items.forEach((item) => {
          if (!state.activityMap.has(item.Id)) {
            state.assigned.push(item.Id);
          }
          state.activityMap.set(item.Id, { value: item });
        });
        applyOrder(state);
        state.counts = calculateCounts(state.activityMap, state.newReplyActivityIds);
      });

      useAgentDrawerHubStore.getState().setIsUpdating(false);
    },

    handleBulkRemove: (items) => {
      set((state) => {
        items.forEach((itemId) => {
          if (state.activityMap.has(itemId)) {
            state.assigned = state.assigned.filter((r) => r != itemId);
          }
          state.activityMap.delete(itemId);
          state.newReplyActivityIds.delete(itemId);
        });
        applyOrder(state);
        state.counts = calculateCounts(state.activityMap, state.newReplyActivityIds);
      });
      useAgentDrawerHubStore.getState().setIsUpdating(false);
    },

    handleRemove: (id) =>
      set((state) => {
        state.activityMap.delete(id);
        state.newReplyActivityIds.delete(id);
        applyOrder(state);
        state.counts = calculateCounts(state.activityMap, state.newReplyActivityIds);
      }),

    updateCounts: () =>
      set((state) => {
        state.counts = calculateCounts(state.activityMap, state.newReplyActivityIds);
      }),

    addNewReply: (activityId) =>
      set((state) => {
        state.newReplyActivityIds.add(activityId);
        state.counts = calculateCounts(state.activityMap, state.newReplyActivityIds);
      }),

    removeNewReply: (activityId) =>
      set((state) => {
        state.newReplyActivityIds.delete(activityId);
        state.counts = calculateCounts(state.activityMap, state.newReplyActivityIds);
      }),

    // UI state methods (preserve existing functionality)
    setFilterModel: (model) =>
      set((state) => {
        state.filterModel = model;
        applyOrder(state);
        state.counts = calculateCounts(state.activityMap, state.newReplyActivityIds);
      }),

    reset: () =>
      set((state) => {
        state.filterModel = {};
        state.expandAllOverride = false;
        applyOrder(state);
        state.counts = calculateCounts(state.activityMap, state.newReplyActivityIds);
      }),

    setUserId: (id) =>
      set((state) => {
        state.userId = id;
      }),

    setExpandAllOverride: (expandAllOverride) =>
      set((state) => {
        state.expandAllOverride = expandAllOverride;
      })
  }))
);
